import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';

/** Backend error body: single { errorKey, message } or multiple { errors: [{ errorKey, message }] } */
interface ApiErrorBody {
  errorKey?: string;
  message?: string;
  errors?: Array<{ errorKey?: string; message?: string }>;
}

@Injectable()
export class ApiErrorToastInterceptor implements HttpInterceptor {
  constructor(
    private messageService: MessageService,
    private translocoService: TranslocoService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          return throwError(() => err);
        }

        const raw = err.error;
        if (raw instanceof Blob) {
          return from(raw.text()).pipe(
            switchMap(text => {
              const body = this.parseBodyFromText(text);
              if (err.status === 400 && this.isServiceResultError(body)) {
                const { summary, detail } = this.parseErrorFromBody(body!);
                this.messageService.add({
                  severity: 'error',
                  summary: detail || undefined,
                  detail: summary,
                  life: 5000,
                });
              }
              return throwError(() => err);
            })
          );
        }

        const body = this.getErrorBody(err);
        if (err.status === 400 && this.isServiceResultError(body)) {
          const { summary, detail } = this.parseErrorFromBody(body!);
          this.messageService.add({
            severity: 'error',
            summary: detail || undefined,
            detail: summary,
            life: 5000,
          });
        }
        return throwError(() => err);
      })
    );
  }

  private isServiceResultError(body: ApiErrorBody | null): boolean {
    if (!body) return false;
    if (body.errorKey != null) return true;
    return Array.isArray(body.errors) && body.errors.length > 0;
  }

  private parseBodyFromText(text: string): ApiErrorBody | null {
    try {
      const parsed = JSON.parse(text);
      return parsed != null && typeof parsed === 'object' ? (parsed as ApiErrorBody) : null;
    } catch {
      return null;
    }
  }

  private parseErrorFromBody(body: ApiErrorBody): { summary: string; detail: string | null } {
    if (body.errors?.length) {
      const first = body.errors[0];
      const errorKey = first.errorKey || 'UNKNOWN';
      return this.summaryDetail(errorKey, first.message ?? null);
    }
    const errorKey = body.errorKey || 'UNKNOWN';
    return this.summaryDetail(errorKey, body.message ?? null);
  }

  private summaryDetail(
    errorKey: string,
    message: string | null,
  ): { summary: string; detail: string | null } {
    const summaryKey = 'errors.' + errorKey;
    const summaryT = this.translocoService.translate(summaryKey);
    const summary = summaryT !== summaryKey ? summaryT : errorKey;

    let detail: string | null = null;
    if (message != null && message.trim()) {
      const detailKey = 'errors.' + message.trim();
      const detailT = this.translocoService.translate(detailKey);
      detail = detailT !== detailKey ? detailT : message;
    }
    return { summary, detail };
  }

  private getErrorBody(err: HttpErrorResponse): ApiErrorBody | null {
    const raw = err.error;
    if (raw == null) return null;
    if (typeof raw === 'object' && (raw.errorKey != null || raw.errors != null || raw.message != null))
      return raw as ApiErrorBody;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw) as ApiErrorBody;
        if (parsed.errorKey != null || parsed.errors != null || parsed.message != null) return parsed;
      } catch {
        return null;
      }
    }
    return null;
  }
}
